import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useSpring, useInView, useReducedMotion } from "framer-motion";
import { cld } from "../../lib/cloudinary";
import { Star } from "./Star";
import "./Editorial.css";

/* ============================================================
   JourneyChapters — the job-seeker "Your Journey" renderer.
   12 steps grouped into 3 phases. Each phase is a cinematic photo
   that STAYS on screen (sticky) while its nested steps scroll past —
   "we walk beside you." The active step lights up and the sticky
   caption tracks "Step 07 of 12".  Sans-only, calm premium motion.
   ============================================================ */

function Step({ step, total, onActive }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-45% 0px -45% 0px", amount: 0.5 });
  const [active, setActive] = useState(false);
  useEffect(() => {
    setActive(inView);
    if (inView) onActive(step.n);
  }, [inView, step.n, onActive]);

  return (
    <li ref={ref} className="jc__step" data-active={active}>
      <span className="jc__dot" aria-hidden="true" />
      <span className="jc__step-num">{String(step.n).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
      <h3 className="jc__step-title">{step.t}</h3>
      <p className="jc__step-body">{step.b}</p>
    </li>
  );
}

function PhaseScene({ phase, index, count, totalSteps, flip }) {
  const sceneRef = useRef(null);
  const [activeNum, setActiveNum] = useState(phase.steps[0].n);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: sceneRef, offset: ["start 0.72", "end 0.6"] });
  const fill = useSpring(scrollYProgress, { stiffness: 90, damping: 26, mass: 0.4 });

  return (
    <div ref={sceneRef} className={`jc__scene ${flip ? "jc__scene--flip" : ""}`}>
      <div className="jc__media">
        <div className="jc__media-inner">
          <img
            src={cld(phase.image, { w: 1100, q: "auto", f: "auto" })}
            srcSet={`${cld(phase.image, { w: 700 })} 700w, ${cld(phase.image, { w: 1100 })} 1100w`}
            sizes="(max-width: 900px) 100vw, 45vw"
            alt={phase.alt}
          />
          <div className="jc__media-scrim" />
          <div className="jc__media-cap">
            <span className="jc__phase-kicker"><Star /> Phase {index + 1} of {count}</span>
            <span className="jc__phase-name">{phase.name}</span>
            <span className="jc__phase-count">Step {String(activeNum).padStart(2, "0")} of {String(totalSteps).padStart(2, "0")}</span>
          </div>
        </div>
      </div>

      <ol className="jc__steps">
        <span className="jc__rail" aria-hidden="true">
          <motion.span className="jc__rail-fill" style={reduce ? { transform: "scaleY(1)" } : { scaleY: fill }} />
        </span>
        {phase.steps.map((s) => (
          <Step key={s.n} step={s} total={totalSteps} onActive={setActiveNum} />
        ))}
      </ol>
    </div>
  );
}

export default function JourneyChapters({ id, eyebrow, chapter, title, note, phases }) {
  const totalSteps = phases.reduce((a, p) => a + p.steps.length, 0);

  return (
    <section id={id} className="ed-band ed-band--cream ed-pad-lg">
      <div className="ed-container">
        {(title || eyebrow) && (
          <header className="ed-opener jc__head">
            <div className="ed-opener__row">
              <span className="ed-eyebrow"><Star className="ed-eyebrow__star" /> {eyebrow}</span>
              {chapter && <span className="ed-chapter">{chapter}</span>}
            </div>
            {title && <h2 className="ed-h2">{title}</h2>}
            {note && <p className="jc__title-note">{note}</p>}
          </header>
        )}

        {phases.map((phase, i) => (
          <PhaseScene
            key={phase.id}
            phase={phase}
            index={i}
            count={phases.length}
            totalSteps={totalSteps}
            flip={i % 2 === 1}
          />
        ))}
      </div>
    </section>
  );
}
